import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Wifi, WifiOff, Cloud, CloudOff, RefreshCw, Download,
  Upload, AlertTriangle, CheckCircle, Clock, Smartphone,
  Bell, BellOff, Settings, HardDrive, Trash2, Eye, 
  Monitor, Tablet, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OfflineSyncManager = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStats, setSyncStats] = useState(null);
  const [syncQueue, setSyncQueue] = useState([]);
  const [devices, setDevices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('sync');
  
  const [preferences, setPreferences] = useState({
    urgent_alerts: true,
    visit_reminders: true,
    team_messages: true,
    patient_updates: true,
    system_notifications: true
  });

  // Demo user ID for testing
  const userId = 'demo_user';
  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('You are back online');
      handleSync();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will be synced when online.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    loadData();
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSyncStats(),
        loadSyncQueue(),
        loadDevices(),
        loadSubscriptions(),
        loadNotificationHistory()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadSyncStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase28/sync/stats/${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncStats(data);
      }
    } catch (error) {
      console.error('Error loading sync stats:', error);
    }
  };

  const loadSyncQueue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase28/sync/queue/${userId}?status=pending`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncQueue(data.items || []);
      }
    } catch (error) {
      console.error('Error loading sync queue:', error);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase28/sync/devices/${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase28/push/subscriptions/${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const loadNotificationHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase28/push/history/${userId}?limit=20`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationHistory(data.notifications || []);
      }
    } catch (error) {
      console.error('Error loading notification history:', error);
    }
  };

  const handleSync = async () => {
    if (!isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }
    
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/phase28/sync/process-all/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Synced ${result.synced} items successfully`);
        if (result.conflicts > 0) {
          toast.warning(`${result.conflicts} items have conflicts`);
        }
        await loadData();
      }
    } catch (error) {
      toast.error('Sync failed');
    }
    setSyncing(false);
  };

  const downloadOfflineData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase28/sync/data-package/${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // In production, this would save to IndexedDB
        localStorage.setItem('offlineData', JSON.stringify(data));
        toast.success(`Downloaded ${data.patients.length} patients for offline use`);
      }
    } catch (error) {
      toast.error('Failed to download offline data');
    }
  };

  const registerDevice = async () => {
    try {
      const deviceInfo = {
        device_name: navigator.platform || 'Web Browser',
        device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        os: navigator.platform,
        browser: navigator.userAgent.split(' ').pop()
      };
      
      const response = await fetch(`${API_URL}/api/phase28/sync/devices/register?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(deviceInfo)
      });
      
      if (response.ok) {
        toast.success('Device registered for offline sync');
        loadDevices();
      }
    } catch (error) {
      toast.error('Failed to register device');
    }
  };

  const enablePushNotifications = async () => {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        toast.error('This browser does not support notifications');
        return;
      }
      
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }
      
      // Register subscription
      const response = await fetch(`${API_URL}/api/phase28/push/subscribe?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          endpoint: 'demo-endpoint-' + Date.now(),
          keys: {},
          device_info: {
            browser: navigator.userAgent.split(' ').pop()
          }
        })
      });
      
      if (response.ok) {
        toast.success('Push notifications enabled');
        loadSubscriptions();
      }
    } catch (error) {
      toast.error('Failed to enable notifications');
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return <Phone className="h-5 w-5" />;
      case 'tablet': return <Tablet className="h-5 w-5" />;
      default: return <Monitor className="h-5 w-5" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="offline-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offline-sync-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {isOnline ? (
              <Cloud className="h-7 w-7 text-green-600" />
            ) : (
              <CloudOff className="h-7 w-7 text-yellow-600" />
            )}
            Offline & Sync Settings
          </h1>
          <p className="text-gray-500">
            Manage offline data and notification preferences
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className={isOnline ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
            {isOnline ? (
              <><Wifi className="h-3 w-3 mr-1" /> Online</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" /> Offline</>
            )}
          </Badge>
          <Button 
            onClick={handleSync} 
            disabled={!isOnline || syncing}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="sync-now-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Sync</p>
                <p className="text-2xl font-bold">{syncStats?.pending || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Conflicts</p>
                <p className="text-2xl font-bold">{syncStats?.conflicts || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Devices</p>
                <p className="text-2xl font-bold">{devices.length}</p>
              </div>
              <Smartphone className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Last Sync</p>
                <p className="text-sm font-medium">
                  {syncStats?.last_sync ? formatDate(syncStats.last_sync).split(',')[0] : 'Never'}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sync">Sync Queue</TabsTrigger>
          <TabsTrigger value="offline">Offline Data</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* Sync Queue Tab */}
        <TabsContent value="sync" className="mt-4">
          {syncQueue.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                <p className="text-gray-500">All data is synced</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {syncQueue.map((item) => (
                <Card key={item.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {item.status === 'conflict' ? (
                          <AlertTriangle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <Clock className="h-5 w-5 text-blue-500" />
                        )}
                        <div>
                          <p className="font-medium capitalize">
                            {item.action} {item.entity_type}
                          </p>
                          <p className="text-sm text-gray-500">
                            Created: {formatDate(item.created_at)}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className={
                        item.priority === 'critical' ? 'border-red-500 text-red-700' :
                        item.priority === 'high' ? 'border-orange-500 text-orange-700' :
                        'border-gray-300'
                      }>
                        {item.priority}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Offline Data Tab */}
        <TabsContent value="offline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                Offline Data Management
              </CardTitle>
              <CardDescription>
                Download data for offline access when you don't have internet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={downloadOfflineData} className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download Offline Data
                </Button>
                <Button variant="outline" onClick={() => {
                  localStorage.removeItem('offlineData');
                  toast.success('Offline data cleared');
                }}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Offline Data
                </Button>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">What's included in offline data:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Your assigned patients</li>
                  <li>• Upcoming visits (next 7 days)</li>
                  <li>• Recent clinical notes (last 30 days)</li>
                  <li>• Medication lists</li>
                  <li>• Documentation templates</li>
                  <li>• Community resources</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="mt-4">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={registerDevice}>
                <Smartphone className="h-4 w-4 mr-2" />
                Register This Device
              </Button>
            </div>
            
            {devices.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Smartphone className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No devices registered</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Register this device to enable offline sync
                  </p>
                </CardContent>
              </Card>
            ) : (
              devices.map((device) => (
                <Card key={device.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gray-100 rounded-full">
                          {getDeviceIcon(device.device_type)}
                        </div>
                        <div>
                          <h3 className="font-medium">{device.device_name}</h3>
                          <p className="text-sm text-gray-500 capitalize">
                            {device.device_type} • {device.os || 'Unknown OS'}
                          </p>
                          <p className="text-xs text-gray-400">
                            Last sync: {formatDate(device.last_sync)}
                          </p>
                        </div>
                      </div>
                      <Badge className={device.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                        {device.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-4">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Push Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={enablePushNotifications}>
                  <Bell className="h-4 w-4 mr-2" />
                  Enable Push Notifications
                </Button>
                
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium">Notification Preferences</h4>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="urgent">Urgent Alerts</Label>
                    <Switch 
                      id="urgent"
                      checked={preferences.urgent_alerts}
                      onCheckedChange={(v) => setPreferences({...preferences, urgent_alerts: v})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="visits">Visit Reminders</Label>
                    <Switch 
                      id="visits"
                      checked={preferences.visit_reminders}
                      onCheckedChange={(v) => setPreferences({...preferences, visit_reminders: v})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="team">Team Messages</Label>
                    <Switch 
                      id="team"
                      checked={preferences.team_messages}
                      onCheckedChange={(v) => setPreferences({...preferences, team_messages: v})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="patient">Patient Updates</Label>
                    <Switch 
                      id="patient"
                      checked={preferences.patient_updates}
                      onCheckedChange={(v) => setPreferences({...preferences, patient_updates: v})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="system">System Notifications</Label>
                    <Switch 
                      id="system"
                      checked={preferences.system_notifications}
                      onCheckedChange={(v) => setPreferences({...preferences, system_notifications: v})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notification History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                {notificationHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No notifications yet</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {notificationHistory.map((notif) => (
                      <div key={notif.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <Bell className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">{notif.title}</p>
                          <p className="text-sm text-gray-600">{notif.body}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(notif.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OfflineSyncManager;
